import React from 'react';
import { useAppContext } from '../App';

const CartSidebar = () => {
  const {
    cart,
    isCartOpen,
    setIsCartOpen,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    cartTotal,
    cartItemCount,
    showNotification
  } = useAppContext();

  if (!isCartOpen) return null;

  const handleCheckout = () => {
    if (cart.length === 0) {
      showNotification('Your cart is empty', 'error');
      return;
    }
    // In production, this would redirect to checkout
    showNotification('Checkout functionality coming soon!', 'info');
  };

  return (
    <>
      <div className="cart-overlay" onClick={() => setIsCartOpen(false)} />
      <div className="cart-sidebar">
        <div className="cart-header">
          <h3 className="cart-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            Shopping Cart
            {cartItemCount > 0 && <span className="cart-badge">{cartItemCount}</span>}
          </h3>
          <button className="cart-close" onClick={() => setIsCartOpen(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="cart-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            <p>Your cart is empty</p>
            <button className="btn btn-outline" onClick={() => setIsCartOpen(false)}>
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="cart-items">
              {cart.map((item) => (
                <div key={item.product.id} className="cart-item">
                  <div className="cart-item-image">
                    <img src={item.product.image} alt={item.product.name} />
                  </div>
                  <div className="cart-item-details">
                    <h4 className="cart-item-name">{item.product.name}</h4>
                    <div className="cart-item-quantity">
                      <button
                        className="qty-btn"
                        onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                      >
                        -
                      </button>
                      <span className="qty-value">{item.quantity}</span>
                      <button
                        className="qty-btn"
                        onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="cart-item-actions">
                    <button
                      className="remove-btn"
                      onClick={() => removeFromCart(item.product.id)}
                      aria-label="Remove item"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="cart-footer">
              <div className="cart-summary">
                <div className="summary-row">
                  <span>Total Items</span>
                  <span>{cartItemCount}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
              </div>

              <div className="cart-actions">
                <button className="btn btn-primary checkout-btn" onClick={handleCheckout}>
                  Proceed to Checkout
                </button>
                <button className="btn btn-outline clear-btn" onClick={clearCart}>
                  Clear Cart
                </button>
              </div>
            </div>
          </>
        )}

        <style jsx>{`
          .cart-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(4px);
            z-index: 200;
            animation: fadeIn 0.2s ease;
          }

          .cart-sidebar {
            position: fixed;
            top: 0;
            right: 0;
            width: 100%;
            max-width: 420px;
            height: 100vh;
            background: linear-gradient(180deg, rgba(30, 41, 59, 0.98), rgba(15, 23, 42, 0.98));
            border-left: 1px solid rgba(59, 130, 246, 0.2);
            z-index: 201;
            display: flex;
            flex-direction: column;
            animation: slideInRight 0.3s ease;
          }

          .cart-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--space-lg) var(--space-xl);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }

          .cart-title {
            display: flex;
            align-items: center;
            gap: var(--space-sm);
            font-family: var(--font-display);
            font-size: var(--font-xl);
            font-weight: 600;
            color: var(--color-neutral-50);
            margin: 0;
          }

          .cart-badge {
            background: var(--color-accent-500);
            color: white;
            font-size: var(--font-xs);
            font-weight: 600;
            padding: 2px 8px;
            border-radius: var(--radius-sm);
            margin-left: var(--space-xs);
          }

          .cart-close {
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.1);
            border: none;
            border-radius: var(--radius-md);
            color: var(--color-neutral-300);
            cursor: pointer;
            transition: all var(--transition-fast);
          }

          .cart-close:hover {
            background: rgba(239, 68, 68, 0.3);
            color: #ef4444;
          }

          .cart-empty {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: var(--space-2xl);
            text-align: center;
            color: var(--color-neutral-400);
          }

          .cart-empty svg {
            opacity: 0.5;
            margin-bottom: var(--space-lg);
          }

          .cart-empty p {
            font-size: var(--font-lg);
            margin-bottom: var(--space-lg);
          }

          .cart-items {
            flex: 1;
            overflow-y: auto;
            padding: var(--space-md);
          }

          .cart-item {
            display: flex;
            gap: var(--space-md);
            padding: var(--space-md);
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.05);
            border-radius: var(--radius-md);
            margin-bottom: var(--space-sm);
          }

          .cart-item-image {
            width: 60px;
            height: 60px;
            border-radius: var(--radius-sm);
            overflow: hidden;
            flex-shrink: 0;
          }

          .cart-item-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .cart-item-details {
            flex: 1;
            min-width: 0;
          }

          .cart-item-name {
            font-size: var(--font-sm);
            font-weight: 500;
            color: var(--color-neutral-100);
            margin: 0 0 var(--space-xs) 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .cart-item-price {
            font-size: var(--font-xs);
            color: var(--color-accent-300);
            margin: 0 0 var(--space-sm) 0;
          }

          .cart-item-quantity {
            display: flex;
            align-items: center;
            gap: var(--space-xs);
          }

          .qty-btn {
            width: 28px;
            height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: var(--radius-sm);
            color: var(--color-neutral-200);
            font-size: var(--font-base);
            cursor: pointer;
            transition: all var(--transition-fast);
          }

          .qty-btn:hover {
            background: rgba(59, 130, 246, 0.2);
            border-color: var(--color-accent-400);
          }

          .qty-value {
            min-width: 32px;
            text-align: center;
            font-family: var(--font-mono);
            font-size: var(--font-sm);
            color: var(--color-neutral-200);
          }

          .cart-item-actions {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            justify-content: space-between;
          }

          .cart-item-total {
            font-family: var(--font-display);
            font-size: var(--font-sm);
            font-weight: 600;
            color: var(--color-neutral-100);
          }

          .remove-btn {
            background: transparent;
            border: none;
            color: var(--color-neutral-500);
            cursor: pointer;
            padding: var(--space-xs);
            transition: color var(--transition-fast);
          }

          .remove-btn:hover {
            color: var(--color-error);
          }

          .cart-footer {
            padding: var(--space-lg) var(--space-xl);
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            background: rgba(0, 0, 0, 0.2);
          }

          .cart-summary {
            margin-bottom: var(--space-lg);
          }

          .summary-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: var(--space-sm);
            font-size: var(--font-sm);
            color: var(--color-neutral-400);
          }

          .summary-row.total {
            padding-top: var(--space-sm);
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            font-size: var(--font-base);
            font-weight: 600;
            color: var(--color-neutral-100);
          }

          .cart-actions {
            display: flex;
            flex-direction: column;
            gap: var(--space-sm);
          }

          .checkout-btn {
            width: 100%;
          }

          .clear-btn {
            width: 100%;
          }

          @keyframes slideInRight {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default CartSidebar;
